import {
  type HTMLAttributes,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
  forwardRef,
  useState,
} from "react";
import {
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { zSticky } from "@/lib/z";
import { cn } from "@/lib/cn";

/* ----------------------------------------------------- styled primitives */

export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="w-full overflow-x-auto">
      <table ref={ref} className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </div>
  ),
);
Table.displayName = "Table";

export const THead = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    style={{ zIndex: zSticky }}
    className={cn("sticky top-0 bg-bg-secondary [&_tr]:border-b [&_tr]:border-border", className)}
    {...props}
  />
));
THead.displayName = "THead";

export const TBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
));
TBody.displayName = "TBody";

export interface TRProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Selected row — accent-subtle background + left accent strip. */
  selected?: boolean;
  /** Row is clickable (hover highlight + pointer). */
  clickable?: boolean;
}

export const TR = forwardRef<HTMLTableRowElement, TRProps>(
  ({ className, selected = false, clickable = false, ...props }, ref) => (
    <tr
      ref={ref}
      data-selected={selected || undefined}
      className={cn(
        "border-b border-border transition-colors",
        clickable && "cursor-pointer hover:bg-bg-elevated/40",
        selected &&
          "bg-accent/15 hover:bg-accent/20 [box-shadow:inset_3px_0_0_0_rgb(var(--color-accent))]",
        className,
      )}
      {...props}
    />
  ),
);
TR.displayName = "TR";

export const TH = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        // whitespace-nowrap: narrow columns were breaking two-word headers
        // one word per line ("Был / в / сети" in a three-line stack).
        "h-10 whitespace-nowrap px-4 text-left align-middle text-xs font-medium text-fg-muted [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  ),
);
TH.displayName = "TH";

export const TD = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn("px-4 py-3 align-middle text-fg-primary [&:has([role=checkbox])]:pr-0", className)}
      {...props}
    />
  ),
);
TD.displayName = "TD";

/* ----------------------------------------------------------- DataTable */

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  /** Open detail on row click. Buttons inside cells must stopPropagation. */
  onRowClick?: (row: TData) => void;
  /** Toolbar slot (search / filters), rendered above the table. */
  toolbar?: ReactNode;
  /** Empty-state shown when there are no rows. */
  empty?: ReactNode;
  /** Enable row selection (drives BulkActionBar). */
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  /** Unique row id accessor for stable selection keys. */
  getRowId?: (row: TData, index: number) => string;
  /** Enable client-side pagination. */
  pagination?: boolean;
  pageSize?: number;
  className?: string;
}

/**
 * DataTable — styled @tanstack/react-table wrapper (DESIGN-SYSTEM §5, §6.1).
 * Sortable headers, row click, toolbar + empty-state slots, optional row
 * selection, pagination.
 */
export function DataTable<TData>({
  columns,
  data,
  onRowClick,
  toolbar,
  empty,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  pagination = false,
  pageSize = 25,
  className,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalSelection, setInternalSelection] = useState<RowSelectionState>({});

  const selection = rowSelection ?? internalSelection;

  const table = useReactTable({
    data,
    columns,
    state: { sorting, rowSelection: selection },
    onSortingChange: setSorting,
    enableRowSelection,
    onRowSelectionChange: (updater) => {
      const next = typeof updater === "function" ? updater(selection) : updater;
      if (onRowSelectionChange) onRowSelectionChange(next);
      else setInternalSelection(next);
    },
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(pagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    initialState: pagination ? { pagination: { pageSize } } : {},
  });

  const rows = table.getRowModel().rows;

  function renderHead() {
    return (
      <THead>
        {table.getHeaderGroups().map((hg) => (
          <tr key={hg.id} className="border-b border-border">
            {hg.headers.map((header) => {
              const canSort = header.column.getCanSort();
              const sortDir = header.column.getIsSorted();
              const meta = header.column.columnDef.meta as
                | { className?: string }
                | undefined;
              return (
                <TH key={header.id} className={meta?.className}>
                  {header.isPlaceholder ? null : canSort ? (
                    <button
                      type="button"
                      onClick={header.column.getToggleSortingHandler()}
                      className="inline-flex items-center gap-1 rounded-sm transition-colors hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sortDir === "asc" ? (
                        <ArrowUp size={13} strokeWidth={2} aria-hidden="true" />
                      ) : sortDir === "desc" ? (
                        <ArrowDown size={13} strokeWidth={2} aria-hidden="true" />
                      ) : (
                        <ArrowUpDown size={13} strokeWidth={2} aria-hidden="true" className="opacity-50" />
                      )}
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                </TH>
              );
            })}
          </tr>
        ))}
      </THead>
    );
  }

  const emptyState = empty ?? <EmptyState title="Нет данных" />;

  return (
    <div className={cn("space-y-3", className)}>
      {toolbar}

      {rows.length === 0 ? (
        <div className="rounded-xl border border-border bg-bg-secondary">{emptyState}</div>
      ) : (
        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full caption-bottom text-sm">
            {renderHead()}
            <TBody>
              {rows.map((row) => (
                <TR
                  key={row.id}
                  clickable={!!onRowClick}
                  selected={row.getIsSelected()}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TD
                      key={cell.id}
                      className={
                        (cell.column.columnDef.meta as { className?: string } | undefined)
                          ?.className
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TD>
                  ))}
                </TR>
              ))}
            </TBody>
          </table>
        </div>
      )}

      {pagination && rows.length > 0 && (
        <div className="flex items-center justify-between gap-3 text-sm text-fg-muted">
          <span>
            Стр. {table.getState().pagination.pageIndex + 1} из {table.getPageCount() || 1}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              Назад
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              Вперёд
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
DataTable.displayName = "DataTable";
